import { useEffect, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { useGetClinicianDashboardQuery } from '../../../api';
import { useDateRange } from '../../../hooks/useDateRange';
import { useTenantIds } from '../../../hooks/useTenantIds';
import { calculatePercentageChange } from '../../../util';

export interface ClinicianDashboardInsights {
  [index: string]: ClinicianDashboardInsight | undefined;
  totalSessions: ClinicianDashboardInsight;
  avgSessionsPerMonth: ClinicianDashboardInsight;
  avgSessionsPerWeek: ClinicianDashboardInsight;
  avgSessionsPerDay: ClinicianDashboardInsight;
  avgRepsPerSession: ClinicianDashboardInsight;
  avgTimeTakenPerSession: ClinicianDashboardInsight;
}

interface ClinicianDashboardInsight {
  value?: number;
  prevValue?: number;
  trend?: number;
}

const sessionsPageSize = 5;

export function useClinicianDashboardData({ dateRange }: { dateRange?: [Date, Date] | null }) {
  const tenantIds = useTenantIds();
  const { dateFrom, dateTo, prevRangeDateFrom, prevRangeDateTo } = useDateRange(dateRange);

  const [filters, setFilters] = useState({ tenantIds, dateRange });
  const [sessionsPage, setSessionsPage] = useState(1);

  if (filters.tenantIds?.length !== tenantIds?.length || filters.dateRange !== dateRange) {
    setFilters({ tenantIds, dateRange });
    setSessionsPage(1);
  }

  const { showBoundary } = useErrorBoundary();

  const { data, isLoading, isFetching, error } = useGetClinicianDashboardQuery({
    tenantIds,
    dateFrom,
    dateTo,
    sessionsPage,
    sessionsPageSize
  });

  useEffect(() => error && showBoundary(error), [error, showBoundary]);

  const {
    data: prevData,
    isLoading: isLoadingPrev,
    isFetching: isFetchingPrev,
    error: prevError
  } = useGetClinicianDashboardQuery({
    tenantIds,
    dateFrom: prevRangeDateFrom,
    dateTo: prevRangeDateTo
  });

  useEffect(() => prevError && showBoundary(prevError), [prevError, showBoundary]);

  const insightsWithTrends = () => {
    if (!data) {
      return undefined;
    }

    return Object.entries(data.insights).reduce((acc, [key, value]) => {
      const prevInsights: Record<string, number | undefined> | undefined = prevData?.insights;
      const prevValue = prevInsights?.[key];
      const hasPrevRange = prevRangeDateFrom !== dateFrom;
      const trend =
        hasPrevRange && value !== undefined && prevValue !== undefined
          ? calculatePercentageChange(value, prevValue)
          : undefined;
      acc[key] = { value, prevValue, trend };
      return acc;
    }, {} as ClinicianDashboardInsights);
  };

  return {
    data: {
      patients: data?.patients ?? [],
      sessions: data?.sessions ?? [],
      totalSessions: data?.totalSessions,
      insights: insightsWithTrends()
    },
    isLoading: isLoading || isLoadingPrev,
    isFetching: isFetching || isFetchingPrev,
    hasMoreSessions: !data || data.sessions.length < data.totalSessions,
    fetchNextPage: () => setSessionsPage((prev) => prev + 1)
  };
}
